import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode } from "swiper";
import "swiper/css";
import { useI18n } from "@/i18n/I18nProvider";

export default function LogosMarquee({
  logos,
  items,
  modelsData,
  renderItem,
  height = 120,
  spaceBetween = 16,
  speed = 1500,
  breakpoints,
  placeholder = "...",
}) {
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const { locale } = useI18n();
  // مصدر الداتا (logos أو items)
  // const data = items || logos || [];

  const data = useMemo(() => {
    if (items?.length) return items;
    if (logos?.length) return logos;

    if (modelsData?.length) return modelsData;

    return [];
  }, [items, logos, modelsData, locale]);
  // تكرار الداتا علشان الحركة اللانهائية
  const repeated = useMemo(() => [...data, ...data], [data]);

  // lazy render
  const sectionRef = useRef(null);
  const [enableSwiper, setEnableSwiper] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setEnableSwiper(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        width: "100%",
        cursor: "grab",
        "& .swiper-button-next, & .swiper-button-prev": {
          display: "none",
        },
      }}
    >
      {enableSwiper ? (
        <Swiper
          modules={prefersReducedMotion ? [] : [Autoplay, FreeMode]}
          loop
          freeMode
          freeModeMomentum={false}
          slidesPerView="auto"
          spaceBetween={spaceBetween}
          speed={prefersReducedMotion ? 0 : speed}
          autoplay={
            prefersReducedMotion
              ? false
              : {
                  delay: 0,
                  disableOnInteraction: false,
                }
          }
          allowTouchMove={false}
          breakpoints={
            breakpoints || {
              0: { slidesPerView: 2 },
              650: { slidesPerView: 3 },
              920: { slidesPerView: 4 },
              1200: { slidesPerView: 6 },
              1600: { slidesPerView: 9 },
            }
          }
        >
          {repeated.map((item, index) => (
            <SwiperSlide key={index}>
              <Box
                sx={{
                  height,
                  minWidth: 120, // مهم للشاشات الصغيرة
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* لو renderItem موجود */}
                {renderItem ? (
                  renderItem(item)
                ) : (
                  // default logo rendering
                  <Box
                    component="img"
                    src={item.img}
                    alt={item.alt || "logo"}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: item.width || 80, // fallback width
                      maxHeight: height - 20,
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                )}
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        // Placeholder ثابت (no CLS)
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            color: "#404456",
          }}
        >
          {placeholder}
        </Box>
      )}
    </Box>
  );
}
